import { FC, useEffect, useRef, useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import OtpInput from "@/components/UI/OtpInput/OtpInput";
import HeaderControl from "../SignUp/components/HeaderControl";
import { styles } from "./stylesConfirmation";
import theme from "@/theme";

type Props = {
  email: string;
  length?: number;
  onSubmit?: (otpCode: string) => void | Promise<void>;
  onResend?: () => void | Promise<void>;
  onHeaderClick: () => void;
};

const OtpForm: FC<Props> = ({ email, length = 6, onSubmit, onResend, onHeaderClick }) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(""));
  const [errorOtp, setErrorOtp] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputsRef = useRef<HTMLInputElement[]>([]);

  useEffect(() => {
    if (otp.join("").length === length) {
      (async () => {
        try {
          setErrorOtp(false);
          if (onSubmit) await onSubmit(otp.join(""));
        } catch {
          setErrorOtp(true);
        }
      })();
    }
  }, [otp]);

  useEffect(() => {
    if (inputsRef.current[0]) inputsRef.current[0].focus();
  }, []);

  useEffect(() => {
    if (errorOtp && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [errorOtp]);

  useEffect(() => {
    if (!resendCooldown) return;
    const interval = setInterval(() => setResendCooldown((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text/plain").toUpperCase();
    const pasteChars = pasteData.split("").filter((c) => /^[0-9A-Z]$/.test(c));
    if (pasteChars.length >= length) {
      const newOtp = [...otp];
      for (let i = 0; i < length; i++) newOtp[i] = pasteChars[i] || "";
      setOtp(newOtp);
    }
  };

  const resendOtp = async () => {
    setOtp(Array(length).fill(""));
    setErrorOtp(false);
    setResendError(null);
    console.log(123);

    try {
      if (onResend) await onResend();
      setResendCooldown(60);
    } catch (e: any) {
      setResendError(e?.message || "Something went wrong");
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <Box sx={styles.wrapper}>
      <HeaderControl onClick={onHeaderClick} logo />

      <Typography
        component="h1"
        variant="h2"
        lineHeight={"26px"}
        fontSize={"22px"}
        color={theme.palette.custom.mainBlack}
        fontWeight={600}
        textAlign={"center"}
        sx={{ marginBottom: "0.375rem", marginTop: "32px" }}
      >
        Verify your email
      </Typography>

      <Typography variant="h4" color="text.secondary" lineHeight={"1.5rem"} textAlign={"center"}>
        We sent you a six digit confirmation code to{" "}
        <Typography component={"span"} color={theme.palette.custom.blueGray800} variant="h4">
          {email}.
        </Typography>{" "}
        Please enter it below to confirm your email address.
      </Typography>

      <Box component="form" sx={{ width: "100%", marginTop: "3.25rem" }}>
        <Box sx={{ marginBottom: "72px" }}>
          <OtpInput
            label="Confirmation code"
            length={length}
            value={otp}
            error={errorOtp}
            helperText={errorOtp ? "Incorrect OTP" : (resendError ?? undefined)}
            onChange={(newOtp) => setOtp(newOtp)}
            onPaste={handlePaste}
          />
        </Box>

        <Typography
          fontFamily="Inter"
          lineHeight="24px"
          fontSize="15px"
          fontWeight={500}
          variant="h5"
          align="center"
          sx={{ mt: 4, color: "#52575D" }}
        >
          Didn't receive a code?{" "}
          {resendCooldown > 0 ? (
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
              <Link underline="always" sx={styles.timer}>
                Send code again ({formatTime(resendCooldown)})
              </Link>
            </Box>
          ) : (
            <Link underline="always" sx={styles.link} onClick={resendOtp}>
              Send code again
            </Link>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default OtpForm;
